import { Box, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { DnsCheckResult } from '@/api/admin/hostnames';
import { useCopy } from '@/hooks/useCopy';

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString();
}

/**
 * SASADM-0148 — extracted verbatim from `TenantHostnames.tsx` (Spec 0028/0033) so
 * the product-console hostnames table can share the same per-row DNS-check result
 * presentation. Behavior-neutral for the tenant screen.
 */
export function DnsRowResult({ result }: { result: DnsCheckResult }) {
  const { t } = useCopy();
  if (!result.configured) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mt: 0.5,
        }}
      >
        {t(
          'hostnames.dns.notConfigured',
          'The platform has no expected DNS target configured yet (a saas-auth ops task).',
        )}
      </Typography>
    );
  }
  if (result.match) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          mt: 0.5,
        }}
      >
        <CheckCircleIcon color="success" fontSize="small" />
        <Typography
          variant="caption"
          sx={{
            color: 'success.main',
          }}
        >
          {t('hostnames.dns.matched', 'DNS verified')}
        </Typography>
      </Stack>
    );
  }
  // Spec 0033 — system/platform diagnostics take precedence over "not propagated".
  if (result.resolutionError) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'error.main',
            display: 'block',
            fontWeight: 600,
          }}
        >
          {t(
            'hostnames.dns.resolutionError',
            'Verification system problem — not your DNS record. Try again shortly.',
          )}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
          }}
        >
          {result.resolutionError}
        </Typography>
      </Box>
    );
  }
  if (result.expectedResolved === false) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'error.main',
          display: 'block',
          mt: 0.5,
          fontWeight: 600,
        }}
      >
        {t(
          'hostnames.dns.expectedResolved',
          "The platform's expected target couldn't be resolved — a platform-side issue. Contact ops.",
        )}
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
        }}
      >
        {t(
          'hostnames.dns.waiting',
          'Not resolving to the expected target yet — DNS can take a while to propagate. Last checked {{checkedAt}}.',
          { checkedAt: fmtTime(result.checkedAt) },
        )}
      </Typography>
      {result.resolvedIps.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
          }}
        >
          {t('hostnames.dns.resolved', 'Resolves to: {{ips}}', {
            ips: result.resolvedIps.join(', '),
          })}
        </Typography>
      )}
    </Box>
  );
}
